/**
 * A single key-indicator metric, styled per the Cloudscape static dashboard
 * pattern's service-overview item: awsui-key-label label, awsui-value-large
 * value (a Link when clickable), with trend, status, and target rendered via
 * Cloudscape components instead of hand-painted styles.
 */
import Box from '@cloudscape-design/components/box';
import Icon from '@cloudscape-design/components/icon';
import Link from '@cloudscape-design/components/link';
import Popover from '@cloudscape-design/components/popover';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';

export interface KeyIndicatorCardProps {
  label: string;
  value: number | string;
  previousValue?: number;
  unit?: string;
  description?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
  higherIsBetter?: boolean;
  trendLabel?: string;
  target?: {
    value: number;
    label?: string;
  };
  onClick?: () => void;
}

const STATUS_TEXT: Record<string, string> = {
  success: 'Good',
  warning: 'Needs attention',
  error: 'Critical',
  info: 'Info',
};

function TrendIndicator({
  current,
  previous,
  higherIsBetter,
  trendLabel,
}: {
  current: number;
  previous: number;
  higherIsBetter: boolean;
  trendLabel?: string;
}) {
  const delta = current - previous;
  if (delta === 0) return null;

  const isUp = delta > 0;
  const isGood = higherIsBetter ? isUp : !isUp;
  const sign = isUp ? '+' : '';
  const displayDelta = Number.isInteger(delta) ? delta : delta.toFixed(1);

  return (
    <Box variant="small" color={isGood ? 'text-status-success' : 'text-status-error'}>
      <Icon name={isUp ? 'caret-up-filled' : 'caret-down-filled'} size="small" variant={isGood ? 'success' : 'error'} />{' '}
      {sign}{displayDelta} {trendLabel ?? 'week over week'}
    </Box>
  );
}

export function KeyIndicatorCard({
  label,
  value,
  previousValue,
  unit,
  description,
  status = 'info',
  higherIsBetter = false,
  trendLabel,
  target,
  onClick,
}: KeyIndicatorCardProps) {
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  const displayValue =
    typeof value === 'number' ? (Number.isInteger(value) ? String(value) : value.toFixed(1)) : value;

  const onTarget =
    target && !isNaN(numericValue)
      ? higherIsBetter
        ? numericValue >= target.value
        : numericValue <= target.value
      : undefined;

  return (
    <SpaceBetween size="xxs">
      <Box variant="awsui-key-label">
        {label}{' '}
        {description && (
          <Popover header={label} content={description} triggerType="custom" size="small">
            <span style={{ cursor: 'help', display: 'inline-flex', verticalAlign: 'middle' }}>
              <Icon name="status-info" size="small" variant="subtle" />
            </span>
          </Popover>
        )}
      </Box>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        {onClick ? (
          <Link
            variant="awsui-value-large"
            href="#"
            onFollow={(e) => {
              e.preventDefault();
              onClick();
            }}
          >
            {displayValue}
          </Link>
        ) : (
          <Box variant="awsui-value-large">{displayValue}</Box>
        )}
        {unit && (
          <Box variant="small" color="text-body-secondary">
            {unit}
          </Box>
        )}
      </div>

      {previousValue !== undefined && !isNaN(numericValue) && (
        <TrendIndicator
          current={numericValue}
          previous={previousValue}
          higherIsBetter={higherIsBetter}
          trendLabel={trendLabel}
        />
      )}

      {target && onTarget !== undefined ? (
        <SpaceBetween direction="horizontal" size="xs">
          <StatusIndicator type={onTarget ? 'success' : 'error'}>
            {onTarget ? 'On target' : 'Off target'}
          </StatusIndicator>
          <Box variant="small" color="text-body-secondary">
            Target: {target.value}{unit ? ` ${unit}` : ''}{target.label ? ` ${target.label}` : ''}
          </Box>
        </SpaceBetween>
      ) : (
        <StatusIndicator type={status}>{STATUS_TEXT[status] ?? 'Info'}</StatusIndicator>
      )}
    </SpaceBetween>
  );
}
